/**
 * Permission strings, exactly as the backend emits them in GET /api/v1/me.
 *
 * Referencing these constants instead of string literals means a typo is a compile
 * error rather than a button that silently never appears.
 *
 * This is UX only. The backend re-checks every request; see
 * ADMIN_WEB_ARCHITECTURE.md §8.
 */

export const PERMISSIONS = {
  INSPECTION_VIEW: 'inspection.view',
  INSPECTION_CREATE: 'inspection.create',
  INSPECTION_ASSIGN: 'inspection.assign',
  INSPECTION_CANCEL: 'inspection.cancel',
  INSPECTION_REOPEN: 'inspection.reopen',
  INSPECTION_SIGN: 'inspection.sign',

  ISSUE_VIEW: 'issue.view',
  ISSUE_MANAGE: 'issue.manage',

  PROPERTY_VIEW: 'property.view',
  PROPERTY_MANAGE: 'property.manage',

  TEMPLATE_VIEW: 'template.view',
  TEMPLATE_CREATE: 'template.create',
  TEMPLATE_EDIT: 'template.edit',
  TEMPLATE_PUBLISH: 'template.publish',

  MEMBER_VIEW: 'member.view',
  MEMBER_MANAGE: 'member.manage',

  REPORT_VIEW: 'report.view',
  REPORT_GENERATE: 'report.generate',
  REPORT_EXPORT: 'report.export',

  SETTINGS_MANAGE: 'settings.manage',

  // Admin Platform vocabulary (brief §2.3).
  COMPANY_VIEW: 'company.view',
  COMPANY_UPDATE: 'company.update',
  COMPANY_MANAGE_BRANDING: 'company.manage_branding',
  USER_VIEW: 'user.view',
  USER_CREATE: 'user.create',
  USER_UPDATE: 'user.update',
  USER_DISABLE: 'user.disable',
  USER_ASSIGN_ROLE: 'user.assign_role',
  PROJECT_VIEW: 'project.view',
  PROJECT_CREATE: 'project.create',
  PROJECT_UPDATE: 'project.update',
  PROJECT_CONFIGURE: 'project.configure',
  TEMPLATE_MAP_FLAT: 'template.map_flat',
  MEASUREMENT_SCOPE_VIEW: 'measurement_scope.view',
  MEASUREMENT_SCOPE_MANAGE: 'measurement_scope.manage',
  ISSUE_TYPE_VIEW: 'issue_type.view',
  ISSUE_TYPE_MANAGE: 'issue_type.manage',
  SNAG_VIEW: 'snag.view',
  SNAG_MANAGE: 'snag.manage',
  ACCESS_CODE_VIEW: 'access_code.view',
  ACCESS_CODE_MANAGE: 'access_code.manage',
  AUDIT_VIEW: 'audit.view',
  ADMIN_MANAGE: 'admin.manage',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

export function hasPermission(granted: readonly string[], required: Permission): boolean {
  return granted.includes(required)
}

/** True when every listed permission is held. Use for actions needing a combination. */
export function hasAllPermissions(
  granted: readonly string[],
  required: readonly Permission[],
): boolean {
  return required.every((permission) => granted.includes(permission))
}

/** True when at least one is held. Use for routes reachable several ways. */
export function hasAnyPermission(
  granted: readonly string[],
  required: readonly Permission[],
): boolean {
  return required.some((permission) => granted.includes(permission))
}

export const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  ops: 'Operations',
  inspector: 'Inspector',
  fm: 'Facility manager',
  viewer: 'Viewer',
}

export const ROLE_DESCRIPTIONS: Record<string, string> = {
  admin:
    'Full control: templates, people, branding, settings and the audit log. Can reopen a reported inspection.',
  ops: 'Runs day-to-day operations: assigns inspections, works the snag board, manages properties.',
  inspector: 'Carries out inspections in the mobile app. Read-only here.',
  fm: 'Handover executive. Signs off inspections and views their own units.',
  viewer: 'Read-only access to inspections, issues and reports.',
}

/** Human label for a permission string, e.g. "inspection.assign" -> "Assign". */
const ACTION_LABELS: Record<string, string> = {
  map_flat: 'Map to flat',
  manage_branding: 'Manage branding',
  assign_role: 'Assign role',
  disable: 'Disable',
  configure: 'Configure',
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  assign: 'Assign',
  cancel: 'Cancel',
  reopen: 'Reopen',
  sign: 'Sign',
  manage: 'Manage',
  publish: 'Publish',
  generate: 'Generate',
  export: 'Export',
}

const MODULE_LABELS: Record<string, string> = {
  company: 'Company',
  user: 'Users',
  project: 'Projects',
  measurement_scope: 'Measurements',
  issue_type: 'Issue types',
  snag: 'Snags',
  access_code: 'Access codes',
  inspection: 'Inspections',
  issue: 'Issues',
  property: 'Properties',
  template: 'Templates',
  member: 'People',
  report: 'Reports',
  settings: 'Settings',
  audit: 'Audit log',
  admin: 'Administration',
  handover: 'Handovers',
}

export function describePermission(permission: string): { module: string; action: string } {
  const [moduleKey = permission, actionKey = ''] = permission.split('.')
  return {
    module: MODULE_LABELS[moduleKey] ?? moduleKey,
    action: ACTION_LABELS[actionKey] ?? actionKey,
  }
}

/** Group permissions by module for the roles matrix, so nobody sees a flat list of 23. */
export function groupPermissions(permissions: readonly string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>()
  for (const permission of permissions) {
    const { module } = describePermission(permission)
    const bucket = groups.get(module)
    if (bucket) bucket.push(permission)
    else groups.set(module, [permission])
  }
  return groups
}
